import { ChangeEvent, useMemo } from 'react'

import Button from 'components/common/button'
import { useQuery } from 'hooks'
import { PageStartIcon, PageEndIcon } from 'components/icons'

type Props = {
  hasNextPage?: boolean
  hasPrevPage?: boolean
  prevPage?: number
  nextPage?: number | null
  currentPage?: number
  totalPages?: number
  limit?: number
  visiblePageNumber?: number
  disabled?: boolean
  shouldDisplayTotalPages?: boolean
  disableGoToLastPage?: boolean
  parentClassName?: string
}

const DataTablePagination = ({
  hasNextPage = false,
  hasPrevPage = false,
  prevPage = 1,
  nextPage = 1,
  currentPage = 1,
  totalPages = 1,
  limit = 10,
  visiblePageNumber = 3,
  disabled = false,
  shouldDisplayTotalPages = true,
  disableGoToLastPage = false,
  parentClassName = '',
}: Props) => {
  const { setQuery, setMultipleQueries } = useQuery()

  const handlePageClick = (page: number) => {
    setQuery('page', page)
  }

  const pageNumberList = useMemo(() => {
    const middlePage = Math.floor(visiblePageNumber / 2)
    const startPage = Math.max(1, currentPage - middlePage)
    const endPage = !nextPage
      ? currentPage
      : Math.min(totalPages, startPage + visiblePageNumber - 1)

    const pages = []

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant="custom"
          className={`rounded-lg px-1.5 ${
            i === currentPage ? 'bg-zinc-200' : ''
          }`}
          type="button"
          buttonText={i}
          onClick={() => handlePageClick(i)}
          disabled={disabled}
        />,
      )
    }

    return pages
  }, [currentPage, totalPages, disabled, hasNextPage])

  return (
    <div
      className={`my-4 flex flex-wrap items-center justify-between gap-2 lg:flex-nowrap ${parentClassName}`}
    >
      <div>
        <span className="text-sm text-black/40">{`Page ${currentPage} ${
          shouldDisplayTotalPages ? ` of ${totalPages}` : ''
        }`}</span>
      </div>

      <div className="flex items-center gap-10">
        <div className="flex items-center gap-3 text-sm text-black">
          <Button
            variant="custom"
            type="button"
            icon={<PageStartIcon />}
            iconClassName="h-[10px] w-[10px]"
            className={`!font-semibold disabled:text-black/40 ${
              !hasPrevPage ? 'pointer-events-none' : ''
            }`}
            disabled={!hasPrevPage || disabled}
            onClick={() => handlePageClick(1)}
          />

          <Button
            variant="custom"
            type="button"
            buttonText="Prev"
            disabled={!hasPrevPage || disabled}
            className={`disabled:text-black/40 ${
              !hasPrevPage ? 'pointer-events-none' : ''
            }`}
            onClick={() => handlePageClick(prevPage)}
          />
        </div>

        <div className="flex items-center gap-1 text-sm leading-5 text-black">
          {pageNumberList}
        </div>

        <div className="flex items-center gap-3 text-sm text-black">
          <Button
            variant="custom"
            type="button"
            buttonText="Next"
            className={`disabled:text-black/40 ${
              !hasNextPage ? 'pointer-events-none' : ''
            }`}
            disabled={!hasNextPage || !nextPage || disabled}
            onClick={() => hasNextPage && nextPage && handlePageClick(nextPage)}
          />

          <Button
            variant="custom"
            type="button"
            icon={<PageEndIcon />}
            iconClassName="h-[10px] w-[10px]"
            className={`font-semibold disabled:text-black/40 ${
              !hasNextPage ? 'pointer-events-none' : ''
            }`}
            disabled={disableGoToLastPage || !hasNextPage || disabled}
            onClick={() => !disableGoToLastPage && handlePageClick(totalPages)}
          />
        </div>
      </div>

      <div>
        <select
          className="bg-transparent text-sm text-black/40"
          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
            setMultipleQueries({
              limit: e.target.value,
              page: 1,
            })
          }}
          disabled={disabled}
          value={limit}
        >
          <option value={10}>Show 10</option>
          <option value={50}>Show 50</option>
          <option value={100}>Show 100</option>
        </select>
      </div>
    </div>
  )
}

export default DataTablePagination
